import { useEffect, useState, useCallback } from "react";
import { toast, Toaster } from "sonner";
import { Copy, Check, LogOut, Plus, Pencil, Trash2, Send, ExternalLink } from "lucide-react";
import { api, formatApiErrorDetail, fmtDate } from "../lib/api";
import { FundingTab, IngestTab } from "./AdminFunding";

const TOPICS = [
  ["eu-financial-regulation", "EU financial regulation"],
  ["company-structuring", "Company structuring"],
  ["cross-border-payments", "Cross-border payments"],
  ["banking-and-account-opening", "Banking and account opening"],
  ["finance-operations", "Finance operations"],
  ["fintech-sector", "Fintech sector"],
  ["eu-funding", "EU funding"],
];

const FORMATS = [
  ["", "General"],
  ["call-alert", "Call alert"],
  ["monthly-digest", "Monthly funding digest"],
  ["deep-guide", "Deep guide"],
  ["cycle-commentary", "Cycle commentary"],
];

const SERVICE_LINKS = [
  ["", "None"],
  ["/services/corporate-structuring|Corporate structuring", "Corporate structuring and market entry"],
  ["/services/finance-operations|Finance operations", "Finance operations"],
  ["/services/cross-border-advisory|Cross-border advisory", "Cross-border advisory"],
  ["/markets/fintech|Fintech", "Fintech (market)"],
];

const EMPTY = { title: "", slug: "", standfirst: "", body: "", topic: "eu-financial-regulation", format: "", language: "en", featured: false, doNotPost: false, linkedInCopy: "", firstTakeaway: "", serviceLinkKey: "" };

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("kairos_token", data.access_token);
      onLogin(data.user);
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    }
  };
  return (
    <div className="min-h-screen flex items-center justify-center bg-paper-alt px-6" data-testid="admin-login-page">
      <form onSubmit={submit} className="bg-paper border border-line rounded-sm p-10 w-full max-w-md">
        <p className="eyebrow mb-4">KairosHK</p>
        <h1 className="k-h3 mb-8">Admin sign in</h1>
        <label className="field-label" htmlFor="a-email">Email</label>
        <input id="a-email" type="email" className="field-input mb-5" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-email-input" />
        <label className="field-label" htmlFor="a-pass">Password</label>
        <input id="a-pass" type="password" className="field-input mb-6" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password-input" />
        {err && <p className="text-sm text-red-600 mb-4" data-testid="admin-login-error">{err}</p>}
        <button type="submit" className="btn-primary w-full justify-center" data-testid="admin-login-btn">Sign in</button>
      </form>
    </div>
  );
};

const StatusPill = ({ status }) => {
  const colors = { queued: "var(--brand)", scheduled: "#8a6d1a", posted: "#4a7c4a", draft: "var(--ink-muted)", failed: "#b03535" };
  return (
    <span className="text-[11px] uppercase tracking-[0.12em] font-semibold px-2 py-1 rounded-sm"
      style={{ color: colors[status] || "var(--ink-muted)", background: "var(--brand-soft)" }}>
      {status}
    </span>
  );
};

const Editor = ({ initial, onSaved, onClose }) => {
  const [f, setF] = useState(initial);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const save = async () => {
    const serviceLink = f.serviceLinkKey
      ? { href: f.serviceLinkKey.split("|")[0], label: f.serviceLinkKey.split("|")[1] }
      : null;
    const payload = {
      title: f.title, slug: f.slug || null, standfirst: f.standfirst, body: f.body, topic: f.topic,
      format: f.format || null, language: f.language || "en",
      featured: f.featured, doNotPost: f.doNotPost, linkedInCopy: f.linkedInCopy || null,
      firstTakeaway: f.firstTakeaway || null, serviceLink,
    };
    try {
      if (f.article_id) await api.put(`/admin/articles/${f.article_id}`, payload);
      else await api.post("/admin/articles", payload);
      toast.success("Article saved");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  return (
    <div className="border border-[var(--accent)] rounded-sm p-8 mb-8 bg-paper" data-testid="article-editor">
      <h3 className="k-h3 mb-6">{f.article_id ? "Edit article" : "New article"}</h3>
      <div className="grid md:grid-cols-2 gap-5 mb-5">
        <div>
          <label className="field-label">Title (under 65 characters)</label>
          <input className="field-input" value={f.title} onChange={set("title")} data-testid="editor-title-input" />
        </div>
        <div>
          <label className="field-label">Slug (auto if blank)</label>
          <input className="field-input" value={f.slug || ""} onChange={set("slug")} data-testid="editor-slug-input" />
        </div>
      </div>
      <label className="field-label">Standfirst (25–40 words — used on cards and the LinkedIn post)</label>
      <textarea rows={2} className="field-input mb-5" value={f.standfirst} onChange={set("standfirst")} data-testid="editor-standfirst-input" />
      <label className="field-label">Body (Markdown: headings, lists, tables, blockquotes)</label>
      <textarea rows={14} className="field-input mb-5 font-mono text-sm" value={f.body} onChange={set("body")} data-testid="editor-body-input" />
      <div className="grid md:grid-cols-2 gap-5 mb-5">
        <div>
          <label className="field-label">Topic</label>
          <select className="field-input" value={f.topic} onChange={set("topic")} data-testid="editor-topic-select">
            {TOPICS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <div>
          <label className="field-label">Contextual service link (end-of-article CTA)</label>
          <select className="field-input" value={f.serviceLinkKey} onChange={set("serviceLinkKey")} data-testid="editor-service-link-select">
            {SERVICE_LINKS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-5 mb-5">
        <div>
          <label className="field-label">Publication format</label>
          <select className="field-input" value={f.format || ""} onChange={set("format")} data-testid="editor-format-select">
            {FORMATS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <div>
          <label className="field-label">Language</label>
          <select className="field-input" value={f.language || "en"} onChange={set("language")} data-testid="editor-language-select">
            <option value="en">English</option>
            <option value="el">Ελληνικά (Greek)</option>
          </select>
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-5 mb-5">
        <div>
          <label className="field-label">First takeaway (second paragraph of LinkedIn post)</label>
          <textarea rows={2} className="field-input" value={f.firstTakeaway || ""} onChange={set("firstTakeaway")} data-testid="editor-takeaway-input" />
        </div>
        <div>
          <label className="field-label">LinkedIn copy override (defaults to standfirst)</label>
          <textarea rows={2} className="field-input" value={f.linkedInCopy || ""} onChange={set("linkedInCopy")} data-testid="editor-linkedin-copy-input" />
        </div>
      </div>
      <div className="flex gap-8 mb-7 text-sm">
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={f.featured} onChange={set("featured")} data-testid="editor-featured-checkbox" /> Featured (pins to top of index)
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={f.doNotPost} onChange={set("doNotPost")} data-testid="editor-donotpost-checkbox" /> Do not post to LinkedIn
        </label>
      </div>
      <div className="flex gap-3">
        <button onClick={save} className="btn-primary" data-testid="editor-save-btn">Save</button>
        <button onClick={onClose} className="btn-secondary" data-testid="editor-cancel-btn">Cancel</button>
      </div>
    </div>
  );
};

const ArticlesTab = () => {
  const [articles, setArticles] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => { api.get("/admin/articles").then(({ data }) => setArticles(data)); }, []);
  useEffect(load, [load]);

  const publish = async (a) => {
    const { data } = await api.post(`/admin/articles/${a.article_id}/publish`);
    toast.success(data.queued ? "Published — LinkedIn queue entry created" : data.firstPublish ? "Published" : "Already published — no duplicate queue entry");
    load();
  };
  const del = async (a) => {
    if (!window.confirm(`Delete "${a.title}"?`)) return;
    await api.delete(`/admin/articles/${a.article_id}`);
    toast.success("Deleted");
    load();
  };
  const edit = (a) => setEditing({
    ...a,
    serviceLinkKey: a.serviceLink ? `${a.serviceLink.href}|${a.serviceLink.label}` : "",
  });

  return (
    <div data-testid="admin-articles-tab">
      {editing ? (
        <Editor initial={editing} onSaved={() => { setEditing(null); load(); }} onClose={() => setEditing(null)} />
      ) : (
        <button onClick={() => setEditing({ ...EMPTY })} className="btn-primary mb-8" data-testid="new-article-btn">
          <Plus size={16} /> New article
        </button>
      )}
      <div className="space-y-0 border-t border-line">
        {articles.map((a) => (
          <div key={a.article_id} className="flex flex-wrap items-center gap-4 py-5 border-b border-line" data-testid={`admin-article-row-${a.slug}`}>
            <div className="flex-1 min-w-[240px]">
              <p className="font-medium text-ink">{a.title}</p>
              <p className="text-xs text-ink-muted mt-1" style={{ fontVariantNumeric: "tabular-nums" }}>
                {a.publishedAt ? `Published ${fmtDate(a.publishedAt)}` : "Draft"} · {a.topic} · {a.readingTime} min
              </p>
            </div>
            <StatusPill status={a.publishedAt ? a.linkedInStatus || "posted" : "draft"} />
            {!a.publishedAt && (
              <button onClick={() => publish(a)} className="btn-primary !py-2 !px-4 text-xs" data-testid={`publish-btn-${a.slug}`}>
                <Send size={13} /> Publish
              </button>
            )}
            {a.publishedAt && (
              <a href={`/insights/${a.slug}`} target="_blank" rel="noreferrer" className="text-brand" title="View" data-testid={`view-btn-${a.slug}`}>
                <ExternalLink size={16} />
              </a>
            )}
            <button onClick={() => edit(a)} className="text-ink-muted hover:text-ink bg-transparent border-0 cursor-pointer" title="Edit" data-testid={`edit-btn-${a.slug}`}>
              <Pencil size={16} />
            </button>
            <button onClick={() => del(a)} className="text-ink-muted hover:text-red-600 bg-transparent border-0 cursor-pointer" title="Delete" data-testid={`delete-btn-${a.slug}`}>
              <Trash2 size={16} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

const BufferPanel = ({ settings, onChange }) => {
  const [form, setForm] = useState({ accessToken: "", organizationId: settings.organizationId || "" });
  const [channels, setChannels] = useState(null);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const payload = { organizationId: form.organizationId || null };
      if (form.accessToken) payload.accessToken = form.accessToken;
      const { data } = await api.put("/admin/buffer/settings", payload);
      onChange(data);
      setForm({ ...form, accessToken: "" });
      toast.success("Buffer settings saved");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setBusy(false);
  };
  const loadChannels = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/admin/buffer/channels");
      setChannels(data);
      if (data.length === 0) toast.info("No connected channels found in Buffer");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setBusy(false);
  };
  const selectChannel = async (c) => {
    const { data } = await api.put("/admin/buffer/settings", { channelId: c.id, channelName: c.displayName || c.name });
    onChange(data);
    toast.success(`Channel selected: ${c.displayName || c.name}`);
  };
  const toggleEnabled = async () => {
    const { data } = await api.put("/admin/buffer/settings", { enabled: !settings.enabled });
    onChange(data);
  };

  return (
    <div className="border border-line rounded-sm p-6 mb-10 bg-paper-alt" data-testid="buffer-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h3 className="k-h3">Buffer scheduling</h3>
        <StatusPill status={settings.configured ? (settings.enabled ? "scheduled" : "queued") : "draft"} />
      </div>
      <p className="text-xs text-ink-muted mb-5 max-w-2xl">
        Connect Buffer to schedule LinkedIn Page posts automatically on publish. Create an access token at publish.buffer.com → Settings → API, and note your organization ID.
      </p>
      <div className="grid md:grid-cols-3 gap-4 mb-4">
        <div>
          <label className="field-label">Access token {settings.configured && <span className="text-brand">(saved)</span>}</label>
          <input type="password" className="field-input" placeholder={settings.configured ? "••••••••" : "Buffer access token"}
            value={form.accessToken} onChange={(e) => setForm({ ...form, accessToken: e.target.value })} data-testid="buffer-token-input" />
        </div>
        <div>
          <label className="field-label">Organization ID</label>
          <input className="field-input" value={form.organizationId}
            onChange={(e) => setForm({ ...form, organizationId: e.target.value })} data-testid="buffer-org-input" />
        </div>
        <div className="flex items-end gap-3">
          <button onClick={save} disabled={busy} className="btn-primary !py-2.5 !px-4 text-xs" data-testid="buffer-save-btn">Save</button>
          <button onClick={loadChannels} disabled={busy || !settings.configured} className="btn-secondary !py-2.5 !px-4 text-xs" data-testid="buffer-load-channels-btn">Load channels</button>
        </div>
      </div>
      {channels && (
        <div className="flex flex-wrap gap-2 mb-4" data-testid="buffer-channel-list">
          {channels.map((c) => (
            <button key={c.id} onClick={() => selectChannel(c)}
              className={`topic-tag !py-2 !px-4 cursor-pointer border-0 ${settings.channelId === c.id ? "!bg-[var(--accent)] !text-white" : ""}`}
              data-testid={`buffer-channel-${c.id}`}>
              {c.service}: {c.displayName || c.name}
            </button>
          ))}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-4 text-sm">
        {settings.channelName && <span className="text-ink-muted">Selected channel: <span className="font-semibold text-ink">{settings.channelName}</span></span>}
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={settings.enabled} onChange={toggleEnabled} disabled={!settings.configured || !settings.channelId} data-testid="buffer-enabled-checkbox" />
          Auto-schedule on publish
        </label>
      </div>
    </div>
  );
};

const QueueTab = () => {
  const [queue, setQueue] = useState([]);
  const [copiedId, setCopiedId] = useState(null);
  const [buffer, setBuffer] = useState(null);
  const load = useCallback(() => { api.get("/admin/queue").then(({ data }) => setQueue(data)); }, []);
  useEffect(() => {
    load();
    api.get("/admin/buffer/settings").then(({ data }) => setBuffer(data)).catch(() => {});
  }, [load]);

  const copy = async (q) => {
    await navigator.clipboard.writeText(q.composedCopy);
    setCopiedId(q.queue_id);
    toast.success("Post copy on clipboard — paste into LinkedIn");
    setTimeout(() => setCopiedId(null), 2000);
  };
  const markPosted = async (q) => {
    const urn = window.prompt("Optional: paste the LinkedIn post URL/URN for write-back", "") || null;
    await api.post(`/admin/queue/${q.queue_id}/mark-posted`, { postUrn: urn });
    toast.success("Marked as posted");
    load();
  };
  const schedule = async (q) => {
    try {
      const { data } = await api.post(`/admin/queue/${q.queue_id}/push-to-buffer`);
      toast.success(`Scheduled via Buffer (post ${data.bufferPostId})`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
    load();
  };

  return (
    <div data-testid="admin-queue-tab">
      {buffer && <BufferPanel settings={buffer} onChange={setBuffer} />}
      <p className="text-sm text-ink-muted mb-8 max-w-2xl">
        {buffer?.configured
          ? "Buffer is connected: schedule items with one click, or let publishing push them automatically when auto-schedule is on. The manual copy-to-clipboard path remains available."
          : "Manual workflow: copy the composed post, publish it on the Kairos LinkedIn Page, then mark it posted. Connect Buffer above for hands-off scheduling."}
        {" "}Editing or republishing an article never creates a duplicate entry.
      </p>
      <div className="space-y-6">
        {queue.length === 0 && <p className="text-ink-muted" data-testid="queue-empty">Queue is empty.</p>}
        {queue.map((q) => (
          <div key={q.queue_id} className="border border-line rounded-sm p-6" data-testid={`queue-item-${q.slug}`}>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <p className="font-medium">{q.title}</p>
              <div className="flex items-center gap-3">
                <StatusPill status={q.status} />
                <span className="text-xs text-ink-muted" style={{ fontVariantNumeric: "tabular-nums" }}>{fmtDate(q.createdAt)}</span>
              </div>
            </div>
            <pre className="whitespace-pre-wrap text-sm bg-paper-alt border border-line rounded-sm p-4 mb-4 font-sans" data-testid={`queue-copy-${q.slug}`}>{q.composedCopy}</pre>
            <div className="flex flex-wrap gap-3">
              <button onClick={() => copy(q)} className="btn-secondary !py-2 !px-4 text-xs" data-testid={`copy-btn-${q.slug}`}>
                {copiedId === q.queue_id ? <Check size={13} /> : <Copy size={13} />} Copy to clipboard
              </button>
              {q.status !== "posted" && q.status !== "scheduled" && buffer?.configured && buffer?.channelId && (
                <button onClick={() => schedule(q)} className="btn-primary !py-2 !px-4 text-xs" data-testid={`schedule-btn-${q.slug}`}>
                  <Send size={13} /> Schedule via Buffer
                </button>
              )}
              {q.status !== "posted" && (
                <button onClick={() => markPosted(q)} className={`${buffer?.configured ? "btn-secondary" : "btn-primary"} !py-2 !px-4 text-xs`} data-testid={`mark-posted-btn-${q.slug}`}>
                  <Check size={13} /> Mark posted
                </button>
              )}
              {q.postUrn && <span className="text-xs text-ink-muted self-center">URN: {q.postUrn}</span>}
              {q.bufferPostId && <span className="text-xs text-ink-muted self-center">Buffer post: {q.bufferPostId}{q.bufferDueAt ? ` · due ${q.bufferDueAt}` : ""}</span>}
              {q.bufferError && <span className="text-xs text-red-600 self-center" data-testid={`buffer-error-${q.slug}`}>{q.bufferError}</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const InboxTab = () => {
  const [contacts, setContacts] = useState([]);
  const [subs, setSubs] = useState([]);
  useEffect(() => {
    api.get("/admin/contacts").then(({ data }) => setContacts(data));
    api.get("/admin/subscribers").then(({ data }) => setSubs(data));
  }, []);
  return (
    <div data-testid="admin-inbox-tab">
      <h3 className="k-h3 mb-5">Contact submissions</h3>
      {contacts.length === 0 && <p className="text-ink-muted text-sm mb-8">No submissions yet.</p>}
      <div className="space-y-4 mb-12">
        {contacts.map((c) => (
          <div key={c.contact_id} className="border border-line rounded-sm p-5 text-sm">
            <div className="flex flex-wrap justify-between gap-2 mb-2">
              <span className="font-medium">{c.name} · {c.email}{c.phone ? ` · ${c.phone}` : ""}{c.company ? ` · ${c.company}` : ""}</span>
              <span className="text-xs text-ink-muted" style={{ fontVariantNumeric: "tabular-nums" }}>{fmtDate(c.createdAt)}</span>
            </div>
            <p className="text-xs text-brand uppercase tracking-[0.1em] mb-2">{c.service}</p>
            <p className="text-ink-muted">{c.message}</p>
          </div>
        ))}
      </div>
      <h3 className="k-h3 mb-5">Subscribers</h3>
      <table className="k-table max-w-2xl">
        <thead><tr><th>Email</th><th>Status</th><th>Lang</th><th>Since</th></tr></thead>
        <tbody>
          {subs.map((s) => (
            <tr key={s.email}>
              <td>{s.email}</td>
              <td><StatusPill status={s.status} /></td>
              <td className="text-ink-muted uppercase">{s.lang || "en"}</td>
              <td className="text-ink-muted" style={{ fontVariantNumeric: "tabular-nums" }}>{fmtDate(s.createdAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {subs.length === 0 && <p className="text-ink-muted text-sm mt-3">No subscribers yet.</p>}
    </div>
  );
};

const TrackerTab = () => {
  const [regimes, setRegimes] = useState([]);
  const load = useCallback(() => { api.get("/regimes").then(({ data }) => setRegimes(data)); }, []);
  useEffect(load, [load]);
  const set = (id, k, v) => setRegimes((rs) => rs.map((r) => (r.regime_id === id ? { ...r, [k]: v } : r)));
  const save = async (r) => {
    try {
      const { data } = await api.put(`/admin/regimes/${r.regime_id}`, { name: r.name, status: r.status, lastReviewed: r.lastReviewed || null });
      toast.success(data.statusChanged ? `Saved — alert emailed to ${data.alerted} subscriber${data.alerted === 1 ? "" : "s"}` : "Saved — status unchanged, no alert sent");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const add = async () => {
    await api.post("/admin/regimes", { name: "New regime", status: "Describe the current status" });
    load();
  };
  const del = async (r) => {
    if (!window.confirm(`Delete "${r.name}"?`)) return;
    await api.delete(`/admin/regimes/${r.regime_id}`);
    toast.success("Deleted");
    load();
  };
  return (
    <div data-testid="admin-tracker-tab">
      <p className="text-sm text-ink-muted mb-6 max-w-2xl">
        This table powers the regulatory perimeter on the Fintech market page. Editing a regime's <span className="font-semibold text-ink">status</span> automatically emails an alert to all confirmed Insights subscribers. Touching only the review date sends nothing.
      </p>
      <button onClick={add} className="btn-primary mb-8" data-testid="add-regime-btn"><Plus size={16} /> Add regime</button>
      <div className="space-y-6">
        {regimes.map((r) => (
          <div key={r.regime_id} className="border border-line rounded-sm p-6" data-testid={`tracker-row-${r.regime_id}`}>
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="field-label">Regime</label>
                <input className="field-input" value={r.name} onChange={(e) => set(r.regime_id, "name", e.target.value)} data-testid={`regime-name-input-${r.regime_id}`} />
              </div>
              <div>
                <label className="field-label">Last reviewed</label>
                <input type="date" className="field-input" value={r.lastReviewed || ""} onChange={(e) => set(r.regime_id, "lastReviewed", e.target.value)} data-testid={`regime-date-input-${r.regime_id}`} />
              </div>
            </div>
            <label className="field-label">Status (changing this sends the alert)</label>
            <textarea rows={2} className="field-input mb-4" value={r.status} onChange={(e) => set(r.regime_id, "status", e.target.value)} data-testid={`regime-status-input-${r.regime_id}`} />
            <div className="flex gap-3">
              <button onClick={() => save(r)} className="btn-primary !py-2 !px-4 text-xs" data-testid={`regime-save-btn-${r.regime_id}`}><Check size={13} /> Save</button>
              <button onClick={() => del(r)} className="btn-secondary !py-2 !px-4 text-xs" data-testid={`regime-delete-btn-${r.regime_id}`}><Trash2 size={13} /> Delete</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const GEO_OPTIONS = [["cy", "Cyprus"], ["gr", "Greece"], ["eu", "EU-level"]];

const CallsTab = () => {
  const [calls, setCalls] = useState([]);
  const load = useCallback(() => { api.get("/admin/grant-calls").then(({ data }) => setCalls(data)); }, []);
  useEffect(load, [load]);
  const set = (id, k, v) => setCalls((cs) => cs.map((c) => (c.call_id === id ? { ...c, [k]: v } : c)));
  const save = async (c) => {
    try {
      await api.put(`/admin/grant-calls/${c.call_id}`, { title: c.title, programme: c.programme, geography: c.geography, deadline: c.deadline, url: c.url || null, summary: c.summary || null });
      toast.success("Call saved");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const add = async () => {
    const deadline = new Date(Date.now() + 30 * 864e5).toISOString().slice(0, 10);
    await api.post("/admin/grant-calls", { title: "New call", programme: "Programme", geography: "cy", deadline });
    load();
  };
  const del = async (c) => {
    if (!window.confirm(`Delete "${c.title}"?`)) return;
    await api.delete(`/admin/grant-calls/${c.call_id}`);
    toast.success("Deleted");
    load();
  };
  const past = new Date().toISOString().slice(0, 10);
  return (
    <div data-testid="admin-calls-tab">
      <p className="text-sm text-ink-muted mb-6 max-w-2xl">
        These calls feed the grant call calendar on the Insights page. Only calls with a deadline of today or later appear publicly; past calls stay here for reference.
      </p>
      <button onClick={add} className="btn-primary mb-8" data-testid="add-call-btn"><Plus size={16} /> Add call</button>
      <div className="space-y-6">
        {calls.map((c) => (
          <div key={c.call_id} className={`border border-line rounded-sm p-6 ${c.deadline < past ? "opacity-60" : ""}`} data-testid={`call-row-${c.call_id}`}>
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="field-label">Title{c.deadline < past ? " (past)" : ""}</label>
                <input className="field-input" value={c.title} onChange={(e) => set(c.call_id, "title", e.target.value)} data-testid={`call-title-input-${c.call_id}`} />
              </div>
              <div>
                <label className="field-label">Programme</label>
                <input className="field-input" value={c.programme} onChange={(e) => set(c.call_id, "programme", e.target.value)} data-testid={`call-programme-input-${c.call_id}`} />
              </div>
            </div>
            <div className="grid md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="field-label">Geography</label>
                <select className="field-input" value={c.geography} onChange={(e) => set(c.call_id, "geography", e.target.value)} data-testid={`call-geo-select-${c.call_id}`}>
                  {GEO_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div>
                <label className="field-label">Deadline</label>
                <input type="date" className="field-input" value={c.deadline} onChange={(e) => set(c.call_id, "deadline", e.target.value)} data-testid={`call-deadline-input-${c.call_id}`} />
              </div>
              <div>
                <label className="field-label">Official URL</label>
                <input className="field-input" value={c.url || ""} onChange={(e) => set(c.call_id, "url", e.target.value)} data-testid={`call-url-input-${c.call_id}`} />
              </div>
            </div>
            <label className="field-label">Summary</label>
            <textarea rows={2} className="field-input mb-4" value={c.summary || ""} onChange={(e) => set(c.call_id, "summary", e.target.value)} data-testid={`call-summary-input-${c.call_id}`} />
            <div className="flex gap-3">
              <button onClick={() => save(c)} className="btn-primary !py-2 !px-4 text-xs" data-testid={`call-save-btn-${c.call_id}`}><Check size={13} /> Save</button>
              <button onClick={() => del(c)} className="btn-secondary !py-2 !px-4 text-xs" data-testid={`call-delete-btn-${c.call_id}`}><Trash2 size={13} /> Delete</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default function Admin() {
  const [user, setUser] = useState(null);
  const [checked, setChecked] = useState(false);
  const [tab, setTab] = useState("articles");

  useEffect(() => {
    api.get("/auth/me").then(({ data }) => setUser(data)).catch(() => {}).finally(() => setChecked(true));
  }, []);

  if (!checked) return <div className="min-h-screen flex items-center justify-center text-ink-muted">Loading…</div>;
  if (!user) return <><Toaster position="top-right" /><Login onLogin={setUser} /></>;

  const tabs = [["articles", "Articles"], ["queue", "LinkedIn queue"], ["funding", "Funding data"], ["ingest", "Ingestion"], ["tracker", "Regulation tracker"], ["calls", "Grant calls"], ["inbox", "Inbox & subscribers"]];
  return (
    <div className="min-h-screen bg-paper" data-testid="admin-dashboard">
      <Toaster position="top-right" />
      <header className="border-b border-line">
        <div className="k-container flex items-center justify-between h-16">
          <div className="flex items-baseline gap-3">
            <span className="text-xl font-bold">KairosHK</span>
            <span className="text-[10px] uppercase tracking-[0.2em] text-ink-muted">Admin</span>
          </div>
          <div className="flex items-center gap-5">
            <a href="/" className="text-sm text-ink-muted no-underline hover:text-ink" data-testid="admin-view-site-link">View site</a>
            <button
              onClick={() => { localStorage.removeItem("kairos_token"); setUser(null); }}
              className="flex items-center gap-2 text-sm text-ink-muted bg-transparent border-0 cursor-pointer hover:text-ink"
              data-testid="admin-logout-btn"
            >
              <LogOut size={15} /> Sign out
            </button>
          </div>
        </div>
      </header>
      <div className="k-container py-10">
        <div className="flex gap-2 mb-10 border-b border-line">
          {tabs.map(([id, label]) => (
            <button key={id} onClick={() => setTab(id)}
              className={`px-5 py-3 text-sm font-medium bg-transparent border-0 border-b-2 cursor-pointer transition-colors ${tab === id ? "border-[var(--accent)] text-ink" : "border-transparent text-ink-muted"}`}
              style={{ borderBottomStyle: "solid" }}
              data-testid={`admin-tab-${id}`}>
              {label}
            </button>
          ))}
        </div>
        {tab === "articles" && <ArticlesTab />}
        {tab === "queue" && <QueueTab />}
        {tab === "funding" && <FundingTab />}
        {tab === "ingest" && <IngestTab />}
        {tab === "tracker" && <TrackerTab />}
        {tab === "calls" && <CallsTab />}
        {tab === "inbox" && <InboxTab />}
      </div>
    </div>
  );
}
